import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { 
  Plus, Edit, Trash2, Search, Filter, Shield, 
  X, AlertTriangle, Eye, Save, Download, Upload, FileText
} from 'lucide-react'
import UserSelect from '../components/UserSelect'
import { format, parseISO, isPast } from 'date-fns'
import Papa from 'papaparse'

const SEVERITIES = ['Low', 'Medium', 'High', 'Critical']
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed']

const safeFormatDate = (dateStr, fmt = 'dd MMM yyyy', fallback = '-') => {
  if (!dateStr) return fallback
  try {
    let parsed = parseISO(dateStr)
    if (isNaN(parsed.getTime())) {
      parsed = new Date(dateStr)
    }
    if (isNaN(parsed.getTime())) return fallback
    return format(parsed, fmt)
  } catch (e) {
    return fallback
  }
}

export function Vulnerabilities({ toast, user }) {
  const qc = useQueryClient()
  const { data: vulnerabilities = [], isLoading } = useQuery({ queryKey: ['vulnerabilities'], queryFn: api.getVulnerabilities })
  const { data: risks = [] } = useQuery({ queryKey: ['risks'], queryFn: api.getRisks })
  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: api.getUsers })

  const [searchTerm, setSearchTerm] = useState('')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortField, setSortField] = useState('id') // 'id', 'date', 'name', 'severity', 'status'
  const [sortOrder, setSortOrder] = useState('asc') // 'asc' or 'desc'

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [viewingItem, setViewingItem] = useState(null)
  const [deletingItem, setDeletingItem] = useState(null)
  const [deleteReason, setDeleteReason] = useState('')

  const createMut = useMutation({
    mutationFn: api.createVulnerability,
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); setShowModal(false); toast('Vulnerability registered', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateVulnerability(id, data),
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); setEditingId(null); toast('Vulnerability updated', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const bulkMut = useMutation({
    mutationFn: api.bulkCreateVulnerabilities,
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); toast('Vulnerabilities imported successfully', 'success') },
    onError: e => toast(e.message, 'error'),
  })
  
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteVulnerability(id, reason),
    onSuccess: () => { qc.invalidateQueries(['vulnerabilities']); toast('Vulnerability moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const isContributor = user?.role === 'Contributor' || user?.role === 'User'
  const allowedVulnerabilities = isContributor ? vulnerabilities.filter(v => v.owner_id === user?.id) : vulnerabilities

  const filtered = allowedVulnerabilities.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (v.custom_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (v.description || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSeverity = severityFilter === 'all' || v.severity === severityFilter
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter
    return matchesSearch && matchesSeverity && matchesStatus
  })

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const sorted = [...filtered].sort((a, b) => {
    let aVal, bVal
    if (sortField === 'id') {
      aVal = a.custom_id || `VULN-${String(a.id).padStart(4, '0')}`
      bVal = b.custom_id || `VULN-${String(b.id).padStart(4, '0')}`
    } else if (sortField === 'date') {
      aVal = a.due_date || '9999-12-31'
      bVal = b.due_date || '9999-12-31'
    } else if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase()
      bVal = (b.name || '').toLowerCase()
    } else if (sortField === 'severity') {
      const sevOrder = { 'Low': 1, 'Medium': 2, 'High': 3, 'Critical': 4 }
      aVal = sevOrder[a.severity] || 0
      bVal = sevOrder[b.severity] || 0
    } else if (sortField === 'status') {
      aVal = (a.status || '').toLowerCase()
      bVal = (b.status || '').toLowerCase()
    } else {
      return 0
    }
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
    return 0
  })

  const exportToCSV = () => {
    const exportData = filtered.map(v => ({
      'Vulnerability ID': v.custom_id,
      'Name': v.name,
      'Description': v.description,
      'Severity': v.severity,
      'Status': v.status,
      'Owner Name': v.owner_name || 'Unassigned',
      'Due Date': safeFormatDate(v.due_date, 'yyyy-MM-dd'),
      'Mapped Risks Count': (() => {
        try {
          const ids = typeof v.risk_ids === 'string' ? JSON.parse(v.risk_ids) : (v.risk_ids || [])
          return Array.isArray(ids) ? ids.length : 0
        } catch { return 0 }
      })(),
      'Mapped Risk IDs': (() => {
        try {
          const ids = typeof v.risk_ids === 'string' ? JSON.parse(v.risk_ids) : (v.risk_ids || [])
          if (!Array.isArray(ids)) return ''
          return ids.map(id => {
            const r = risks.find(x => x.id === id)
            return r ? (r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`) : `RSK-${String(id).padStart(4, '0')}`
          }).join(', ')
        } catch { return '' }
      })()
    }))
    
    const csv = Papa.unparse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `Vulnerabilities_${format(new Date(), 'yyyy-MM-dd')}.csv`)
    link.click()
  }

  const downloadTemplate = () => {
    const headers = [
      'Name', 'Description', 'Severity', 'Status', 'Owner Name', 'Due Date', 'Mapped Risk IDs'
    ]
    const example = [
      'Outdated SSL Certificate', 'The server is running TLS 1.0 which is outdated and insecure.', 'High', 'Open', 'Ashok Kumar A', '2026-05-14', 'RSK-0001, RSK-0002'
    ]
    const csv = Papa.unparse([headers, example])
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'vulnerabilities_upload_template.csv'
    a.click()
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const mapped = results.data
          .filter(row => row['Name'] && row['Name'].trim()) // Skip rows with empty names
          .map(row => {
            // Map Owner Name to user ID if possible
            const ownerNameClean = row['Owner Name']?.trim()?.toLowerCase() || ''
            const foundUser = users.find(u => {
              const fullName = (u.full_name || '').trim().toLowerCase()
              const username = (u.username || '').trim().toLowerCase()
              return fullName === ownerNameClean || username === ownerNameClean
            })

            // Map Risk IDs (comma-separated string, e.g. "RSK-0001, RSK-0002") to numeric database ids
            const mappedRiskIds = []
            const riskIdsStr = row['Mapped Risk IDs'] || row['mapped_risk_ids'] || ''
            if (riskIdsStr.trim()) {
              const parts = riskIdsStr.split(',').map(p => p.trim().toLowerCase())
              parts.forEach(part => {
                if (!part) return
                // Find matching risk by custom_id OR RSK-padded string OR database ID
                const foundRisk = risks.find(r => {
                  const customId = (r.custom_id || '').toLowerCase()
                  const formattedId = `rsk-${String(r.id).padStart(4, '0')}`
                  return customId === part || formattedId === part || String(r.id) === part
                })
                if (foundRisk) {
                  mappedRiskIds.push(foundRisk.id)
                }
              })
            }
            
            // Normalize due_date to YYYY-MM-DD
            let rawDueDate = row['Due Date']?.trim() || row['due_date']?.trim() || null
            let parsedDueDate = null
            if (rawDueDate) {
              const isoPattern = /^\d{4}-\d{2}-\d{2}$/
              if (isoPattern.test(rawDueDate)) {
                parsedDueDate = rawDueDate
              } else {
                const slashParts = rawDueDate.split('/')
                const dashParts = rawDueDate.split('-')
                if (slashParts.length === 3) {
                  const [part1, part2, part3] = slashParts
                  if (part3.length === 4) {
                    const day = parseInt(part1, 10)
                    const month = parseInt(part2, 10)
                    const year = parseInt(part3, 10)
                    if (month > 12 && day <= 12) {
                      parsedDueDate = `${year}-${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}`
                    } else {
                      parsedDueDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                    }
                  } else if (part1.length === 4) {
                    parsedDueDate = `${part1}-${part2.padStart(2, '0')}-${part3.padStart(2, '0')}`
                  }
                } else if (dashParts.length === 3 && dashParts[0].length <= 2) {
                  const [part1, part2, part3] = dashParts
                  if (part3.length === 4) {
                    const day = parseInt(part1, 10)
                    const month = parseInt(part2, 10)
                    const year = parseInt(part3, 10)
                    if (month > 12 && day <= 12) {
                      parsedDueDate = `${year}-${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}`
                    } else {
                      parsedDueDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                    }
                  }
                }
                
                if (!parsedDueDate) {
                  try {
                    const d = new Date(rawDueDate)
                    if (!isNaN(d.getTime())) {
                      const offset = d.getTimezoneOffset()
                      const adjustedDate = new Date(d.getTime() - (offset * 60 * 1000))
                      parsedDueDate = adjustedDate.toISOString().split('T')[0]
                    }
                  } catch (e) {
                    parsedDueDate = null
                  }
                }
              }
            }

            return {
              custom_id: null, // Auto-pick and generate sequential ID in backend
              name: row['Name']?.trim() || 'Unnamed Vulnerability',
              description: row['Description']?.trim() || '',
              severity: ['Low', 'Medium', 'High', 'Critical'].includes(row['Severity']?.trim()) ? row['Severity']?.trim() : 'Medium',
              status: ['Open', 'In Progress', 'Resolved', 'Closed'].includes(row['Status']?.trim()) ? row['Status']?.trim() : 'Open',
              owner_id: foundUser?.id || null,
              due_date: parsedDueDate,
              risk_ids: mappedRiskIds
            }
          })
        if (mapped.length > 0) {
          bulkMut.mutate(mapped)
        } else {
          toast('No valid vulnerabilities found in CSV', 'error')
        }
      }
    })
    e.target.value = '' // Reset input
  }

  if (isLoading) return <div className="page"><div className="loading">Loading vulnerabilities...</div></div>

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Vulnerabilities</h1>
          <p className="page-subtitle">Track and manage identified security vulnerabilities.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={downloadTemplate}>
            <Download size={15} /> Template
          </button>
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <FileText size={15} /> Export CSV
          </button>
          <div style={{ position: 'relative' }}>
            <button className="btn btn-secondary" onClick={() => document.getElementById('vuln-csv').click()}>
              <Upload size={15} /> Bulk Upload
            </button>
            <input type="file" id="vuln-csv" style={{ display: 'none' }} accept=".csv" onChange={handleFileUpload} />
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={15} /> New Vulnerability
          </button>
        </div>
      </div>

      <div style={{ 
        background: 'var(--bg-elevated)', borderRadius: 16, padding: '16px 24px', 
        border: '1px solid var(--border)', marginBottom: 24, display: 'flex', gap: 24, alignItems: 'end',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
             Search
          </label>
          <div style={{ position: 'relative' }}>
            <Search size={16} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: 10 }} />
            <input 
              type="text" 
              className="form-input"
              style={{ paddingLeft: 36, width: '100%', height: 36 }}
              placeholder="Search by ID, name, or description..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Filter size={12} style={{ marginRight: 6 }} /> Severity
          </label>
          <select className="form-select" style={{ width: 160, height: 36, padding: '0 12px' }} value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
            <option value="all">All Severities</option>
            {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, display: 'block', textTransform: 'uppercase' }}>
            <Filter size={12} style={{ marginRight: 6 }} /> Status
          </label>
          <select className="form-select" style={{ width: 160, height: 36, padding: '0 12px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }} className="hover:text-primary">
                ID <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>{sortField === 'id' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
              </th>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }} className="hover:text-primary">
                Name <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>{sortField === 'name' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
              </th>
              <th onClick={() => handleSort('severity')} style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }} className="hover:text-primary">
                Severity <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>{sortField === 'severity' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
              </th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }} className="hover:text-primary">
                Status <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>{sortField === 'status' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
              </th>
              <th>Owner</th>
              <th onClick={() => handleSort('date')} style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }} className="hover:text-primary">
                Due Date <span style={{ fontSize: '0.65rem', marginLeft: 4 }}>{sortField === 'date' ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}</span>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan="7" className="text-center" style={{ padding: '32px' }}>No vulnerabilities found</td></tr>
            ) : sorted.map(v => (
              <tr key={v.id}>
                <td><strong>{v.custom_id}</strong></td>
                <td>{v.name}</td>
                <td>
                  <span className={`badge ${
                    v.severity === 'Critical' ? 'badge-danger' : 
                    v.severity === 'High' ? 'badge-warning' : 
                    v.severity === 'Medium' ? 'badge-info' : 'badge-neutral'
                  }`}>{v.severity}</span>
                </td>
                <td>
                  <span className={`badge ${
                    v.status === 'Resolved' || v.status === 'Closed' ? 'badge-success' :
                    v.status === 'In Progress' ? 'badge-warning' : 'badge-danger'
                  }`}>{v.status}</span>
                </td>
                <td>{v.owner_name || 'Unassigned'}</td>
                <td>{safeFormatDate(v.due_date, 'dd MMM yyyy')}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setViewingItem(v)} title="View"><Eye size={14} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingId(v.id)} title="Edit"><Edit size={14} /></button>
                    {user?.role === 'Admin' && (
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => {
                        setDeletingItem(v)
                        setDeleteReason('')
                      }} title="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showModal && <VulnerabilityModal risks={risks} onClose={() => setShowModal(false)} onSave={d => createMut.mutate(d)} isSaving={createMut.isPending} user={user} />}
      {editingId && <VulnerabilityModal vulnId={editingId} initialData={vulnerabilities.find(v => v.id === editingId)} risks={risks} onClose={() => setEditingId(null)} onSave={d => updateMut.mutate({ id: editingId, data: d })} isSaving={updateMut.isPending} user={user} />}

      {viewingItem && (
        <ViewModal 
          item={viewingItem}
          risks={risks}
          onClose={() => setViewingItem(null)} 
          onEdit={() => { setEditingId(viewingItem.id); setViewingItem(null) }} 
        />
      )}

      {deletingItem && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '440px', padding: '32px', borderRadius: '24px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px' }}>
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '12px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={24} style={{ color: '#EF4444' }} />
              </div>
              
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>Move to Bin?</h3>
              
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                Delete "{deletingItem.name}"? It will be moved to the Recycle Bin and can be restored later.
              </p>

              <div style={{ width: '100%', textAlign: 'left', marginTop: '8px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', display: 'block' }}>
                  Justification / Reason (Required)
                </label>
                <textarea
                  className="form-input"
                  style={{ 
                    width: '100%', 
                    minHeight: '80px', 
                    borderRadius: '8px', 
                    background: 'var(--bg-main)', 
                    border: '1px solid var(--border)', 
                    color: 'var(--text-main)', 
                    padding: '12px',
                    fontSize: '0.875rem',
                    resize: 'none',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10B981';
                    e.target.style.boxShadow = '0 0 0 2px rgba(16, 185, 129, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Why is this being deleted?"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', width: '100%', gap: '12px', marginTop: '16px' }}>
                <button 
                  className="btn" 
                  style={{ 
                    flex: 1, 
                    background: 'var(--bg-main)', 
                    border: '1px solid var(--border)', 
                    color: 'var(--text-main)',
                    borderRadius: '8px',
                    height: '40px',
                    fontWeight: 600
                  }} 
                  onClick={() => setDeletingItem(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn" 
                  disabled={!deleteReason.trim()}
                  style={{ 
                    flex: 1, 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid #F87171', 
                    color: '#FCA5A5',
                    borderRadius: '8px',
                    height: '40px',
                    fontWeight: 600,
                    opacity: !deleteReason.trim() ? 0.5 : 1,
                    cursor: !deleteReason.trim() ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    deleteMut.mutate({ id: deletingItem.id, reason: deleteReason })
                    setDeletingItem(null)
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function VulnerabilityModal({ vulnId, initialData = null, onClose, onSave, isSaving, risks = [], user }) {
  const src = initialData || {}

  const [form, setForm] = useState({
    name: src.name || '',
    description: src.description || '',
    severity: src.severity || 'Medium',
    status: src.status || 'Open',
    owner_id: src.owner_id || null,
    due_date: src.due_date || '',
    risk_ids: (() => {
      try { return typeof src.risk_ids === 'string' ? JSON.parse(src.risk_ids) : (src.risk_ids || []) }
      catch { return [] }
    })()
  })

  const [showRiskDropdown, setShowRiskDropdown] = useState(false)
  const [riskSearch, setRiskSearch] = useState('')

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const handleSave = () => {
    onSave(form)
  }

  const toggleRisk = (rId) => {
    update('risk_ids', form.risk_ids.includes(rId) 
      ? form.risk_ids.filter(id => id !== rId)
      : [...form.risk_ids, rId]
    )
  }

  if (vulnId && !initialData) return null

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-header">
          <h2>{vulnId ? 'Edit Vulnerability' : 'Add Vulnerability'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-group">
            <label className="form-label">Vulnerability Name *</label>
            <input 
              className="form-input" 
              value={form.name} 
              onChange={e => update('name', e.target.value)} 
              placeholder="e.g., Outdated SSL Certificate"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea 
              className="form-textarea" 
              value={form.description} 
              onChange={e => update('description', e.target.value)} 
              style={{ minHeight: '80px' }}
            />
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Severity</label>
              <select className="form-select" value={form.severity} onChange={e => update('severity', e.target.value)}>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => update('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input 
                type="date" 
                className="form-input" 
                value={form.due_date ? form.due_date.split('T')[0] : ''} 
                onChange={e => update('due_date', e.target.value)} 
              />
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <UserSelect value={form.owner_id} onChange={val => update('owner_id', val)} disabled={user?.role === 'Contributor'} />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '20px', position: 'relative' }}>
            <label className="form-label">Map to Risks</label>
            <div 
              className="form-input"
              style={{ 
                background: 'var(--bg-elevated)', minHeight: 42, display: 'flex', 
                alignItems: 'center', justifyContent: 'between', flexWrap: 'wrap', 
                gap: 6, padding: '6px 12px', cursor: 'pointer', border: '1px solid var(--border)',
                borderRadius: 8
              }}
              onClick={() => setShowRiskDropdown(!showRiskDropdown)}
            >
              {form.risk_ids.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, width: '90%' }}>
                  {form.risk_ids.map(rId => {
                    const r = risks.find(x => x.id === rId)
                    const rIdDisplay = r?.custom_id || `RSK-${String(rId).padStart(4, '0')}`
                    return (
                      <span key={rId} className="badge badge-accent" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px' }}>
                        <strong>{rIdDisplay}</strong> - {r?.description || 'Unnamed Risk'}
                        <X size={12} style={{ cursor: 'pointer', marginLeft: 4 }} onClick={(e) => { e.stopPropagation(); toggleRisk(rId) }} />
                      </span>
                    )
                  })}
                </div>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Map risks to this vulnerability...</span>
              )}
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: 'auto' }}>{showRiskDropdown ? '▲' : '▼'}</span>
            </div>

            {showRiskDropdown && (
              <div style={{ 
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101, 
                background: 'var(--bg-modal)', border: '1px solid var(--border)', 
                borderRadius: 12, marginTop: 8, maxHeight: 220, overflowY: 'auto',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ padding: 10, borderBottom: '1px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--bg-modal)', zIndex: 102 }}>
                  <input 
                    autoFocus
                    className="form-input" 
                    placeholder="Search risks by ID or description..." 
                    value={riskSearch} 
                    onChange={e => setRiskSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    style={{ height: 34, fontSize: '0.8125rem', width: '100%', padding: '0 12px' }}
                  />
                </div>
                <div style={{ padding: '4px 0' }}>
                  {risks.filter(r => {
                    const idDisplay = r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`
                    return idDisplay.toLowerCase().includes(riskSearch.toLowerCase()) || 
                           r.description.toLowerCase().includes(riskSearch.toLowerCase())
                  }).length > 0 ? risks.filter(r => {
                    const idDisplay = r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`
                    return idDisplay.toLowerCase().includes(riskSearch.toLowerCase()) || 
                           r.description.toLowerCase().includes(riskSearch.toLowerCase())
                  }).map(r => {
                    const rIdDisplay = r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`
                    const isSelected = form.risk_ids.includes(r.id)
                    return (
                      <div 
                        key={r.id} 
                        style={{ 
                          padding: '10px 16px', cursor: 'pointer', transition: 'background 0.2s', 
                          borderBottom: '1px solid var(--border-light)', display: 'flex', 
                          justifyContent: 'space-between', alignItems: 'center',
                          background: isSelected ? 'var(--bg-elevated)' : 'transparent'
                        }}
                        onClick={(e) => { e.stopPropagation(); toggleRisk(r.id) }}
                      >
                        <div style={{ textAlign: 'left' }}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--accent)', display: 'block', textTransform: 'uppercase', marginBottom: 2 }}>{rIdDisplay}</span>
                          <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-primary)' }}>{r.description}</span>
                        </div>
                        {isSelected && <span style={{ color: 'var(--accent)', fontWeight: 'bold', fontSize: '1rem' }}>✓</span>}
                      </div>
                    )
                  }) : (
                    <div style={{ padding: 15, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>No risks found.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !form.name.trim()}>
            {isSaving ? 'Saving...' : 'Save Vulnerability'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ViewModal({ item, onClose, onEdit, risks }) {
  const mappedRisks = (() => {
    try {
      const ids = typeof item.risk_ids === 'string' ? JSON.parse(item.risk_ids) : []
      return risks.filter(r => ids.includes(r.id))
    } catch { return [] }
  })()

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: 'var(--accent)22', color: 'var(--accent)', padding: 8, borderRadius: 8 }}>
              <Shield size={18} />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Vulnerability Details</h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>View vulnerability configuration</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-sm" onClick={onEdit}><Edit size={14} /> Edit</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div><label className="form-label">ID</label><p style={{ fontWeight: 600 }}>{item.custom_id}</p></div>
            <div>
              <label className="form-label">Status</label>
              <span className={`badge ${
                    item.status === 'Resolved' || item.status === 'Closed' ? 'badge-success' :
                    item.status === 'In Progress' ? 'badge-warning' : 'badge-danger'
                  }`}>{item.status}</span>
            </div>
            <div>
              <label className="form-label">Severity</label>
              <span className={`badge ${
                    item.severity === 'Critical' ? 'badge-danger' : 
                    item.severity === 'High' ? 'badge-warning' : 
                    item.severity === 'Medium' ? 'badge-info' : 'badge-neutral'
                  }`}>{item.severity}</span>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label className="form-label">Name</label>
            <p>{item.name}</p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label className="form-label">Description</label>
            <p className="text-muted">{item.description || 'No description provided.'}</p>
          </div>

          <div style={{ display: 'flex', gap: '40px', marginBottom: '16px' }}>
            <div>
              <label className="form-label">Owner</label>
              <p>{item.owner_name || 'Unassigned'}</p>
            </div>
            <div>
              <label className="form-label">Due Date</label>
              <p>{safeFormatDate(item.due_date, 'dd MMM yyyy', 'None')}</p>
            </div>
          </div>

          <div>
            <label className="form-label">Mapped Risks</label>
            {mappedRisks.length > 0 ? (
              <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '0.875rem' }}>
                {mappedRisks.map(r => (
                  <li key={r.id} style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{r.custom_id || `RSK-${String(r.id).padStart(4, '0')}`}</span>{' - '}{r.description}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted" style={{ fontSize: '0.875rem' }}>No risks mapped.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
